import { useEffect, useRef } from 'react';

/*
 * Live microphone level for the published audio producer.
 *
 * The stage used to fake this: .bn-levels ran a 900ms CSS keyframe loop, so the bars in the
 * name chip bounced at a constant rate whether the teacher was talking, sitting silent or
 * muted. This measures the actual track instead, and everything that shows "I am talking"
 * reads off the one measurement.
 *
 * Two values come out of a single requestAnimationFrame loop, and both are written straight
 * to the DOM on `rootRef` rather than through state. The broadcast page is a 2500-line
 * component; re-rendering it sixty times a second to move a ring is not a trade worth
 * making, and transform/opacity driven off a CSS variable stays on the compositor.
 *
 *   --bn-mic      0..1 smoothed level. Set on the shell, so it inherits to the ring around
 *                 the avatar, the tile outline and the chip bars alike.
 *   .bn-speaking  the steady "this person is talking" state, with hysteresis so it does not
 *                 strobe in the gaps between words.
 *   .bn-mic-live  present only while the analyser is actually running, so the old keyframe
 *                 bars stay the fallback anywhere this hook is not driving (the lobby).
 */

const FLOOR_DB = -60;         // quieter than this is not worth measuring at all
// Every room has a noise of its own — a fan, a street, the mic's own hiss with AGC winding
// it up — and a fixed floor cannot tell that apart from a voice. Measuring -60dB..0dB onto
// 0..1 put ordinary room tone around 0.2..0.3, which was enough to light the wave and trip
// "speaking" in an empty room. The floor follows the room instead, and what gets measured
// is how far above it this frame sits.
const NOISE_MARGIN_DB = 12;   // a voice has to clear the room's own noise by this much
const SPEAK_RANGE_DB = 24;    // ...and this much over it is a full meter
// Both slow, and only about five to one apart. They were 0.25 against 0.0008, which does not
// find the room's noise at all: falling that fast makes the floor chase the quietest single
// frame rather than the level the room actually sits at, and a room tone that wanders a few
// dB between frames ends up with a floor 10-15dB under its own average. Everything above it
// then clears the margin, so silence read as speech, the floor froze — it only tracks while
// nobody is talking — and the wave stayed lit for the rest of the class.
const NOISE_FALL = 0.01;      // settles to a new, quieter room over about a second...
const NOISE_RISE = 0.002;     // ...and follows it up more slowly than that
// The floor is frozen during speech so a long sentence cannot drag it up into the voice.
// Frozen absolutely, though, a floor that started too low could never recover — so it may
// still creep up while speaking, slowly enough to be invisible inside a sentence and surely
// enough that a stuck "speaking" always unsticks itself.
const NOISE_RISE_SPEAKING = 0.0004;
const SPEAK_ON = 0.28;        // level that starts "speaking" — ~19dB over the room
const SPEAK_OFF = 0.12;       // level it has to drop below to stop being speaking...
const RELEASE_MS = 700;       // ...and stay below for this long, so pauses do not flicker
const RELEASE_DECAY = 0.82;   // the meter falls slower than it rises, like a VU needle
const REBIND_MS = 700;        // how often to look for the track appearing or being swapped

export default function useMicLevel(rootRef, getStream, active) {
    const levelRef = useRef(0);
    const speakingRef = useRef(false);
    // getStream closes over roomClient and is rebuilt every render; keeping it in a ref lets
    // the effect depend on `active` alone instead of tearing the audio graph down and
    // rebuilding it on every unrelated re-render.
    const getStreamRef = useRef(getStream);
    getStreamRef.current = getStream;

    useEffect(() => {
        const clear = () => {
            levelRef.current = 0;
            speakingRef.current = false;
            const el = rootRef.current;
            if (!el) return;
            el.style.setProperty('--bn-mic', '0');
            el.classList.remove('bn-speaking', 'bn-mic-live');
        };

        if (!active) {
            // Muting pauses the producer, which only sets track.enabled = false — the
            // analyser would read a flat line and the meter would decay to zero on its own.
            // Dropping it to zero here instead makes the bars die on the same frame as the
            // click, which is what "mic off" should look like.
            clear();
            return undefined;
        }

        let ctx = null;
        let analyser = null;
        let source = null;
        let buf = null;
        let raf = null;
        let boundTrackId = null;
        let quietSince = 0;
        let stopped = false;
        // The room's own noise, in dB. Seeded from the first frame rather than from a
        // constant: starting at -60 in a room that sits at -45 would take twenty seconds to
        // creep up to the truth, and the wave would be lit for all of it.
        let noiseDb = null;

        const teardownGraph = () => {
            if (raf) cancelAnimationFrame(raf);
            raf = null;
            try { source?.disconnect(); } catch (e) { /* graph already gone */ }
            try { ctx?.close(); } catch (e) { /* already closed */ }
            ctx = null; analyser = null; source = null; buf = null; boundTrackId = null;
            // A different track is a different room as far as the floor is concerned — a
            // device change or a reconnect can land on another mic with another hiss.
            noiseDb = null;
        };

        const setSpeaking = (value) => {
            if (value === speakingRef.current) return;
            speakingRef.current = value;
            rootRef.current?.classList.toggle('bn-speaking', value);
        };

        const tick = () => {
            raf = requestAnimationFrame(tick);
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
                const v = (buf[i] - 128) / 128;
                sum += v * v;
            }
            const rms = Math.sqrt(sum / buf.length);
            // Loudness is perceived in dB, and linear RMS spends nearly all of its range on
            // silence: a normal speaking voice sits around 0.05, so a raw 0..1 ring would
            // barely move.
            const db = rms > 0 ? Math.max(20 * Math.log10(rms), FLOOR_DB) : FLOOR_DB;

            // An analyser whose buffer has not filled yet reads a flat 128 — rms 0, and a
            // seed of -60dB that the floor would then spend ten seconds climbing out of.
            // Wait for a real sample instead.
            if (noiseDb === null) {
                if (rms <= 0) return
                noiseDb = db;
            }
            const rate = db < noiseDb
                ? (speakingRef.current ? 0 : NOISE_FALL)
                : (speakingRef.current ? NOISE_RISE_SPEAKING : NOISE_RISE);
            noiseDb += (db - noiseDb) * rate;

            // How far this frame stands out from the room, not how loud it is in absolute
            // terms: the same voice on a hissy laptop mic and a good headset now reads the
            // same, and neither room's idle noise reads as anything at all.
            const over = db - noiseDb - NOISE_MARGIN_DB;
            const raw = Math.min(Math.max(over / SPEAK_RANGE_DB, 0), 1);
            const prev = levelRef.current;
            const level = raw > prev ? raw : (prev * RELEASE_DECAY) + (raw * (1 - RELEASE_DECAY));
            levelRef.current = level;

            const now = performance.now();
            if (level >= SPEAK_ON) {
                quietSince = 0;
                setSpeaking(true);
            } else if (speakingRef.current) {
                if (level >= SPEAK_OFF) {
                    quietSince = 0;
                } else {
                    if (!quietSince) quietSince = now;
                    if (now - quietSince >= RELEASE_MS) setSpeaking(false);
                }
            }

            // Publish the level only while the speaking decision holds. Everything that
            // reads --bn-mic — the wave behind the avatar, the bars in the name chip — is
            // meant to say "this person is talking", and a bare level says "this mic is on":
            // the two look identical to anyone watching, and the second one is never off.
            // Hysteresis already carries the gap between two words, so nothing drops out
            // mid-sentence; between sentences it goes quiet, which is the point.
            rootRef.current?.style.setProperty(
                '--bn-mic',
                speakingRef.current ? level.toFixed(3) : '0',
            );
        };

        // Runs on an interval as well as up front: the audio producer is created
        // asynchronously after the publish chain finishes, and reconnectProducer replaces
        // the track outright, so there is no single moment to hook onto.
        const bind = () => {
            if (stopped) return;
            const el = rootRef.current;
            if (!el) return;
            const stream = getStreamRef.current?.();
            const track = stream?.getAudioTracks?.()?.[0];
            if (!track || track.readyState !== 'live') {
                if (boundTrackId) { teardownGraph(); clear(); }
                return;
            }
            if (track.id === boundTrackId) return;
            teardownGraph();
            try {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                ctx = new Ctx();
                // A fresh single-track stream rather than the published one: that stream
                // also carries the camera, and swapVideoToStill replaces its video track
                // mid-call, which is enough to make Safari rebuild the source node.
                source = ctx.createMediaStreamSource(new MediaStream([track]));
                analyser = ctx.createAnalyser();
                analyser.fftSize = 512;
                analyser.smoothingTimeConstant = 0.4;
                buf = new Uint8Array(analyser.fftSize);
                // Deliberately not connected on to ctx.destination — that would put the mic
                // through the speakers. An analyser pulls samples without needing a sink.
                source.connect(analyser);
                ctx.resume?.();
                boundTrackId = track.id;
                el.classList.add('bn-mic-live');
                raf = requestAnimationFrame(tick);
            } catch (e) {
                console.log('mic level unavailable', e);
                teardownGraph();
            }
        };

        bind();
        const poll = setInterval(bind, REBIND_MS);
        return () => {
            stopped = true;
            clearInterval(poll);
            teardownGraph();
            clear();
        };
    }, [active, rootRef]);

    return { levelRef, speakingRef };
}
